/**
 * @component ry-timeline
 * @category  display
 * @summary   Vertical (default) or horizontal timeline of events. Authored as a
 *            wrapper with `<ry-timeline-item>` children — each item renders a
 *            three-cell row (start / middle / end) with a connecting line running
 *            through the middle column (or row, for horizontal). The middle cell
 *            holds a coloured status dot; `time` and `title` attributes drop into
 *            the start and end cells; remaining slotted content flows under the
 *            title for body copy. Ported from daisyUI v5 `.timeline`.
 *
 * Reference: daisyUI v5 `timeline.css` (MIT) —
 *   https://github.com/saadeghi/daisyui/blob/master/packages/daisyui/src/components/timeline.css
 * Daisy uses a `<ul>` of `<li>` with `.timeline-start`/`.timeline-middle`/
 * `.timeline-end` children and `<hr>` connector segments. ry-timeline keeps the
 * same three-cell grid + connector idea, but exposes it as a declarative
 * wrapper + items so consumers don't have to hand-author the cells.
 *
 * License: MIT — https://github.com/saadeghi/daisyui/blob/master/LICENSE
 *
 * @attribute direction   'vertical' | 'horizontal'   default='vertical'   Layout axis. Vertical stacks items top-to-bottom with the line running down the middle column. Horizontal lays items left-to-right with the line running along the middle row.
 * @attribute align       'left' | 'right' | 'center'   default='center'   For vertical timelines, controls where each item's title/body sits relative to the connector line. `center` alternates start/end on each side (daisy default); `left` puts every title on the right of the line (time on the left); `right` mirrors that. Has no effect for horizontal timelines.
 *
 * @content   Each direct `<ry-timeline-item>` becomes one row. Anything you put
 *            inside the item beyond the `time` + `title` attributes becomes
 *            the slotted body content rendered below the title in the end cell.
 *
 * @dom
 *   ry-timeline[direction][align]                   role="list"
 *     > ry-timeline-item[time][title][status]       role="listitem"  data-ry-target="item"
 *         > span         [data-ry-target="start"]   .ry-timeline-item__start    -- time label
 *         > span         [data-ry-target="middle"]  .ry-timeline-item__middle   -- status dot
 *         > span         [data-ry-target="end"]     .ry-timeline-item__end      -- title + slotted body
 *             > strong   [data-ry-target="title"]   .ry-timeline-item__title
 *             > slot                                                          -- consumer body
 *
 * @gotcha  `direction` and `align` only have meaning on the parent `<ry-timeline>`.
 *          Setting them on a child `<ry-timeline-item>` is a no-op.
 *
 * @gotcha  Setup-once: dynamically inserted `<ry-timeline-item>` children that
 *          existed in markup at mount-time render correctly. Items appended
 *          AFTER setup work too — `<ry-timeline-item>` is its own custom
 *          element and runs its own `setup()` on connect. But if you mutate
 *          an item's `time`/`title`/`status` attribute after mount, the
 *          rendered cells re-sync via `attributeChangedCallback`.
 *
 * @gotcha  `status="default"` is the same as omitting `status` — both paint the
 *          dot with `--ry-color-primary`. Use one of `success`, `warning`,
 *          `danger`, `info` to recolor; unknown values fall back to the
 *          default primary tone.
 *
 * @example basic
 * ```html
 * <ry-timeline>
 *   <ry-timeline-item time="2024" title="Shipped"></ry-timeline-item>
 *   <ry-timeline-item time="2025" title="Iterated"></ry-timeline-item>
 *   <ry-timeline-item time="2026" title="Released"></ry-timeline-item>
 * </ry-timeline>
 * ```
 *
 * @example vertical-left
 * ```html
 * <ry-timeline align="left">
 *   <ry-timeline-item time="9:00" title="Standup"></ry-timeline-item>
 *   <ry-timeline-item time="10:30" title="Design review"></ry-timeline-item>
 *   <ry-timeline-item time="14:00" title="Ship"></ry-timeline-item>
 * </ry-timeline>
 * ```
 *
 * @example horizontal
 * ```html
 * <ry-timeline direction="horizontal">
 *   <ry-timeline-item time="Q1" title="Plan"></ry-timeline-item>
 *   <ry-timeline-item time="Q2" title="Build"></ry-timeline-item>
 *   <ry-timeline-item time="Q3" title="Launch"></ry-timeline-item>
 *   <ry-timeline-item time="Q4" title="Grow"></ry-timeline-item>
 * </ry-timeline>
 * ```
 *
 * @example status-variants
 * ```html
 * <ry-timeline>
 *   <ry-timeline-item time="Mon" title="Default" status="default"></ry-timeline-item>
 *   <ry-timeline-item time="Tue" title="Success" status="success"></ry-timeline-item>
 *   <ry-timeline-item time="Wed" title="Warning" status="warning"></ry-timeline-item>
 *   <ry-timeline-item time="Thu" title="Danger"  status="danger"></ry-timeline-item>
 *   <ry-timeline-item time="Fri" title="Info"    status="info"></ry-timeline-item>
 * </ry-timeline>
 * ```
 *
 * @example mixed-content-with-body
 * ```html
 * <ry-timeline>
 *   <ry-timeline-item time="2024-01-04" title="Filed application">
 *     Submitted the initial paperwork for review.
 *   </ry-timeline-item>
 *   <ry-timeline-item time="2024-02-12" title="Approved" status="success">
 *     Cleared compliance and moved to staging.
 *   </ry-timeline-item>
 *   <ry-timeline-item time="2024-03-30" title="Live" status="success">
 *     Production rollout complete; monitoring metrics.
 *   </ry-timeline-item>
 * </ry-timeline>
 * ```
 *
 * @tutorial renders-three-cells-per-item
 * ```js
 * const el = mount(`
 *   <ry-timeline>
 *     <ry-timeline-item time="a" title="A"></ry-timeline-item>
 *     <ry-timeline-item time="b" title="B"></ry-timeline-item>
 *   </ry-timeline>
 * `);
 * const items = el.querySelectorAll('[data-ry-target="item"]');
 * await expectEqual(items.length, 2, 'two items present');
 * const firstStart = items[0].querySelector('[data-ry-target="start"]');
 * const firstMiddle = items[0].querySelector('[data-ry-target="middle"]');
 * const firstEnd = items[0].querySelector('[data-ry-target="end"]');
 * await expectEqual(firstStart && firstMiddle && firstEnd ? true : false, true, 'three cells');
 * ```
 *
 * @tutorial direction-attribute-applied
 * ```js
 * const el = mount('<ry-timeline direction="horizontal"><ry-timeline-item time="x" title="X"></ry-timeline-item></ry-timeline>');
 * await expectAttr(el, 'direction', 'horizontal');
 * ```
 *
 * @tutorial status-on-item-tags-dot
 * ```js
 * const el = mount(`
 *   <ry-timeline>
 *     <ry-timeline-item time="x" title="X" status="success"></ry-timeline-item>
 *   </ry-timeline>
 * `);
 * const item = el.querySelector('ry-timeline-item');
 * await expectAttr(item, 'status', 'success');
 * const middle = item.querySelector('[data-ry-target="middle"]');
 * // Dot color is driven via the status attribute on the host; CSS reads it.
 * await expectEqual(!!middle, true, 'middle cell rendered');
 * ```
 *
 * @tutorial time-and-title-render-into-correct-cells
 * ```js
 * const el = mount('<ry-timeline><ry-timeline-item time="2030" title="Done"></ry-timeline-item></ry-timeline>');
 * const item = el.querySelector('ry-timeline-item');
 * const start = item.querySelector('[data-ry-target="start"]');
 * const title = item.querySelector('[data-ry-target="title"]');
 * await expectEqual(start.textContent.trim(), '2030', 'time renders into start cell');
 * await expectEqual(title.textContent.trim(), 'Done', 'title renders into end cell');
 * ```
 *
 * @tutorial host-and-items-get-list-aria
 * ```js
 * const el = mount('<ry-timeline><ry-timeline-item time="x" title="X"></ry-timeline-item></ry-timeline>');
 * await expectAttr(el, 'role', 'list', 'host gets role=list');
 * const item = el.querySelector('ry-timeline-item');
 * await expectAttr(item, 'role', 'listitem', 'item gets role=listitem');
 * ```
 *
 * @see ry-step
 * @see ry-list
 */

import { RyElement } from '../core/ry-element.js';

export class RyTimeline extends RyElement {
  setup(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'list');
    if (!this.hasAttribute('direction')) this.setAttribute('direction', 'vertical');
    if (!this.hasAttribute('align')) this.setAttribute('align', 'center');
  }
}

/**
 * @component ry-timeline-item
 * @category  display
 * @summary   Single row inside a `<ry-timeline>`. Renders three cells: `time`
 *            label (start), status dot (middle), and `title` + slotted body
 *            (end). Status colour is driven by the `status` attribute.
 *
 * @attribute time     string?                                                 Short label rendered in the start cell. Free-form — date, time, era, anything.
 * @attribute title    string?                                                 Bold label rendered in the end cell, above the slotted body.
 * @attribute status   'default' | 'success' | 'warning' | 'danger' | 'info'   default='default'   Colour of the middle dot. Pulls from the token palette.
 *
 * @content   Free-form HTML — rendered in the end cell below the title.
 *
 * @see ry-timeline
 */
export class RyTimelineItem extends RyElement {
  static observedAttributes = ['time', 'title', 'status'] as const;

  setup(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'listitem');
    if (!this.hasAttribute('data-ry-target')) this.setAttribute('data-ry-target', 'item');
    this.#render();
  }

  attributeChangedCallback(): void {
    if (!this.isConnected) return;
    this.#syncCells();
  }

  /**
   * Build the three-cell scaffold once. Slotted user content is moved into the
   * end cell beneath the rendered <strong> title. Re-rendering would clobber
   * any user-driven DOM mutations inside the body, so we only render when the
   * scaffold isn't there yet — subsequent attribute changes update cell text
   * in place.
   */
  #render(): void {
    if (this.$('[data-ry-target="start"]')) {
      this.#syncCells();
      return;
    }

    // Preserve original slotted children (anything beyond the attribute-driven
    // time/title labels) and reinsert them as the body of the end cell.
    const slotted = Array.from(this.childNodes);

    const start = document.createElement('span');
    start.setAttribute('data-ry-target', 'start');
    start.className = 'ry-timeline-item__start';

    const middle = document.createElement('span');
    middle.setAttribute('data-ry-target', 'middle');
    middle.className = 'ry-timeline-item__middle';
    // Inner dot — kept as a child so theme CSS can target it without
    // depending on a pseudo-element (consumers may want to slot an icon
    // later by overriding via [data-ry-target="middle"]).
    const dot = document.createElement('span');
    dot.setAttribute('data-ry-target', 'dot');
    dot.className = 'ry-timeline-item__dot';
    middle.appendChild(dot);

    const end = document.createElement('span');
    end.setAttribute('data-ry-target', 'end');
    end.className = 'ry-timeline-item__end';

    const title = document.createElement('strong');
    title.setAttribute('data-ry-target', 'title');
    title.className = 'ry-timeline-item__title';
    end.appendChild(title);

    const body = document.createElement('span');
    body.setAttribute('data-ry-target', 'body');
    body.className = 'ry-timeline-item__body';
    slotted.forEach((node) => body.appendChild(node));
    end.appendChild(body);

    // Clear any leftover whitespace then mount the scaffold.
    this.innerHTML = '';
    this.appendChild(start);
    this.appendChild(middle);
    this.appendChild(end);

    this.#syncCells();
  }

  #syncCells(): void {
    const start = this.$('[data-ry-target="start"]');
    const title = this.$('[data-ry-target="title"]');
    if (start) start.textContent = this.getAttribute('time') ?? '';
    if (title) title.textContent = this.getAttribute('title') ?? '';
  }
}

customElements.define('ry-timeline', RyTimeline);
customElements.define('ry-timeline-item', RyTimelineItem);
